import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { AddDocumentsPage } from 'src/data-capture/file-management/AddDocumentsPage';
import FilesPage from 'src/data-capture/file-management/FilesPage';
import SuitabilityReports from 'src/data-capture/file-management/SuitabilityReports';
import MeetingAgendas from 'src/data-capture/file-management/MeetingAgendas';
import MeetingNotes from 'src/data-capture/file-management/MeetingNotes';
import FactFinds from 'src/data-capture/file-management/FactFinds';
import SiestaAIGenerated from 'src/data-capture/file-management/SiestaAIGenerated';

export const FileUploadTabNames = {
  ToReview: 'add',
  Reviewed: 'files',
  SuitabilityReports: 'suitability-reports',
  MeetingAgendas: 'meeting-agendas',
  MeetingNotes: 'meeting-notes',
  FactFinds: 'fact-finds',
  SiestaAIGenerated: 'generated',
} as const;

type FileUploadTabName = (typeof FileUploadTabNames)[keyof typeof FileUploadTabNames];

function isTabName(value?: string): value is FileUploadTabName {
  if (!value) {
    return false;
  }

  return Object.values(FileUploadTabNames).includes(value as FileUploadTabName);
}

export default function FileUploadPage() {
  const { sectionSlug: tab, folderId } = useParams<{ sectionSlug: string; folderId?: string }>();
  const { clientId } = useCurrentClient();

  const isFolderRoute = tab?.startsWith('folder/') || !!folderId;
  const selectedTab = isFolderRoute ? FileUploadTabNames.Reviewed : tab;

  if (!isFolderRoute && !isTabName(selectedTab)) {
    return <Navigate to={`/client/${clientId}/document/${FileUploadTabNames.ToReview}`} replace />;
  }

  switch (selectedTab) {
    case FileUploadTabNames.ToReview:
      return <AddDocumentsPage />;
    case FileUploadTabNames.Reviewed:
      return <FilesPage />;
    case FileUploadTabNames.SuitabilityReports:
      return <SuitabilityReports />;
    case FileUploadTabNames.MeetingAgendas:
      return <MeetingAgendas />;
    case FileUploadTabNames.MeetingNotes:
      return <MeetingNotes />;
    case FileUploadTabNames.FactFinds:
      return <FactFinds />;
    case FileUploadTabNames.SiestaAIGenerated:
      return <SiestaAIGenerated />;
    default:
      return (
        <Navigate to={`/client/${clientId}/document/${FileUploadTabNames.ToReview}`} replace />
      );
  }
}
